import {useHierarchy, type KpiDashboardLocationType, type KpiDashboardPlant} from '@domain/kpis'
import {findLocationEntry, HierarchyNodeType} from '@domain/shared'
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import {STOPPAGE_ANALYSIS_SUB_PATH, stoppageAnalysisSubRoute} from '@features/stoppage-analysis'
import {FeatureErrorBoundary} from '@hconnect/common/components'
import {DataContentWrapper} from '@hconnect/common/containers'
import {FeatureTranslationsLoader} from '@hconnect/common/localization-loaders'
import {NotificationProvider, useFundamentalMuiTheme, useNotification} from '@hconnect/uikit'
import {PageContainer} from '@hconnect/uikit/src/lib2'
import {useUpmPlantsContext} from '@hconnect/upm'
import {TextRegular} from '@hm/fundamental'
import {
  buildFeatureUrl,
  useGlobalHierarchyId,
  useIsHierarchyPickerSwitch,
  usePlantHierarchyQuery,
  useSetBreadcrumbLabel,
  usePlantFromHierarchyId,
  useSuiteHierarchyId
} from '@hproduce/suite-core'
import {ThemeProvider} from '@mui/material'
import {LocalizationProvider} from '@mui/x-date-pickers'
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment'
import {ReactNode, Suspense, useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {
  generatePath,
  Navigate,
  Outlet,
  RouteObject,
  useLocation,
  useParams,
  useResolvedPath
} from 'react-router'

import {FEATURE_NAMESPACE, PERFORMANCE_REPORTS_FEATURE_NAME, PLANT_STATUS_SUB_PATH} from '../consts'
import {useKpiDashboardAccess} from '../hooks/api'
import {useSuiteKpiLocation} from '../hooks/useSuiteKpiLocation'
import {performanceReportsFeatureTranslations} from '../localization'
import {ActualVsPlannedPage, PlantStatusPage} from '../pages'
import {ActualVsPlannedErrorSource, ActualVsPlannedRoutes} from '../types'
import {getEasyPomDashboardPath, getShiftSummaryPath, getStoppagesListPath} from '../utils'
import {
  findSuiteHierarchyNode,
  findSuiteNodeForKpiLocation,
  getKpiLocationPath,
  isKpiLocationWithinSuiteNode
} from '../utils/hierarchy-mapping.utils'

const parentLocationLabelKeys: Record<KpiDashboardLocationType, string> = {
  area: 'goBackLabels.backToArea',
  region: 'goBackLabels.backToRegion',
  country: 'goBackLabels.backToCountry',
  plant: 'goBackLabels.back'
}

/**
 * Builds the URL of a KPI location below `baseUrl`: `<area>` for areas and
 * `<area>/<location>` below them, so Suite's breadcrumb reads "area | country" and the area crumb
 * leads back to the area overview.
 */
const useKpiLocationUrl = () => {
  const {data: kpiHierarchy} = useHierarchy()

  return useCallback(
    (baseUrl: string, locationId: string) => {
      const path = kpiHierarchy ? getKpiLocationPath(kpiHierarchy, locationId) : []
      const segments = path.length ? path.map(({id}) => id) : [locationId]
      return `${baseUrl}/${segments.join('/')}`
    },
    [kpiHierarchy]
  )
}

/**
 * KPI location overviews with the picker matching the page. The Suite hierarchy holds only the
 * nodes the user has access to, so:
 * - `getSuiteNodeId` finds the user's Suite node mapping to a location
 * - `isWithinNode` tells whether a location is part of a Suite node (e.g. "US Midwest" of US)
 * - `getUrl` opens the location's node, or, for KPI-only locations without one, the location below
 *   `fallbackHierarchyId` (default: the Global node) if it is part of it. Undefined when the user
 *   has no access to the location, so links and back buttons to it are hidden.
 */
const useKpiLocationSuiteUrl = () => {
  const globalHierarchyId = useGlobalHierarchyId()
  const getKpiLocationUrl = useKpiLocationUrl()
  const {data: suiteHierarchy, isLoading: isSuiteHierarchyLoading} = usePlantHierarchyQuery()
  const {data: kpiHierarchy, isLoading: isKpiHierarchyLoading} = useHierarchy()
  const plantsMap = useUpmPlantsContext({isInitialLoad: true})

  const getPlantId = useCallback(
    (hierarchyId: string) =>
      Object.values(plantsMap ?? {}).find((plant) => plant.upmId === hierarchyId)?.plantId,
    [plantsMap]
  )

  const getSuiteNodeId = useCallback(
    (locationId: string): string | undefined =>
      suiteHierarchy && kpiHierarchy
        ? findSuiteNodeForKpiLocation(suiteHierarchy, kpiHierarchy, getPlantId, locationId)
            ?.hierarchyId
        : undefined,
    [suiteHierarchy, kpiHierarchy, getPlantId]
  )

  const isWithinNode = useCallback(
    (locationId: string, hierarchyId: string | null): boolean => {
      const node =
        suiteHierarchy && hierarchyId
          ? findSuiteHierarchyNode(suiteHierarchy, hierarchyId)
          : undefined
      return (
        !!node &&
        !!kpiHierarchy &&
        isKpiLocationWithinSuiteNode(node, kpiHierarchy, getPlantId, locationId)
      )
    },
    [suiteHierarchy, kpiHierarchy, getPlantId]
  )

  const getUrl = useCallback(
    (locationId: string, fallbackHierarchyId = globalHierarchyId): string | undefined => {
      const nodeId = getSuiteNodeId(locationId)
      if (nodeId) {
        return buildFeatureUrl(PERFORMANCE_REPORTS_FEATURE_NAME, nodeId)
      }
      if (fallbackHierarchyId && isWithinNode(locationId, fallbackHierarchyId)) {
        const baseUrl = buildFeatureUrl(PERFORMANCE_REPORTS_FEATURE_NAME, fallbackHierarchyId)
        return getKpiLocationUrl(baseUrl, locationId)
      }
      return undefined
    },
    [globalHierarchyId, getKpiLocationUrl, getSuiteNodeId, isWithinNode]
  )

  return {
    getUrl,
    getSuiteNodeId,
    isWithinNode,
    isLoading: isSuiteHierarchyLoading || isKpiHierarchyLoading || !plantsMap
  }
}

/**
 * URL of a plant's parent area / region / country overview, given like in the legacy HProduce app:
 * only to users with more than one plant whose KPI access includes that location, and only when a
 * Suite node shows that overview. Undefined otherwise, so the back button and the crumbs to it are
 * hidden.
 */
const useParentLocationUrl = () => {
  const {getUrl, isLoading} = useKpiLocationSuiteUrl()
  const {data: access, isLoading: isAccessLoading} = useKpiDashboardAccess()

  const getParentUrl = useCallback(
    (locationId: string): string | undefined => {
      const hasMultiplePlants =
        access?.status === 'multiLocationsAccess' || (access?.accessiblePlantIds?.length ?? 0) > 1
      const isAccessible = !!access?.accessibleLocations?.includes(locationId)
      return hasMultiplePlants && isAccessible ? getUrl(locationId) : undefined
    },
    [access, getUrl]
  )

  return {getParentUrl, isLoading: isLoading || isAccessLoading}
}

/**
 * Back link from a plant to its parent location overview, see `useParentLocationUrl`. Keeps the
 * query (e.g. the time range) like the overview's own back links.
 */
const useParentLocationBackButton = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {getParentUrl} = useParentLocationUrl()
  const {search} = useLocation()

  return useCallback(
    ({parentLocationId, parentLocationType}: KpiDashboardPlant) => {
      const target = parentLocationId ? getParentUrl(parentLocationId) : undefined
      if (!target) return undefined

      return {
        targetName: t(parentLocationLabelKeys[parentLocationType] ?? 'goBackLabels.back'),
        target: `${target}${search}`
      }
    },
    [getParentUrl, search, t]
  )
}

const SuitePlantStatusPage = ({isFeaturePlantView = false}) => {
  const upmPlant = usePlantFromHierarchyId()
  const getParentLocationBackButton = useParentLocationBackButton()

  if (!upmPlant) {
    return <PageLoader />
  }

  const stoppagesListPath = getStoppagesListPath(upmPlant.plantId, upmPlant.upmId)
  const shiftSummaryPath = getShiftSummaryPath(upmPlant.upmId)

  return (
    <PlantStatusPage
      plantId={upmPlant.plantId}
      easyPomLinkPath={getEasyPomDashboardPath(upmPlant.upmId)}
      stoppageListHref={stoppagesListPath}
      shiftSummaryHref={shiftSummaryPath}
      getBackButtonProps={isFeaturePlantView ? getParentLocationBackButton : undefined}
      // Open Stoppage Analysis at the feature level, so its breadcrumb starts with it instead of
      // carrying the plant's "Area | Country | Plant" segments
      getStoppageAnalysisPath={
        isFeaturePlantView
          ? (equipmentId) =>
              `${buildFeatureUrl(PERFORMANCE_REPORTS_FEATURE_NAME, upmPlant.upmId)}/${generatePath(
                STOPPAGE_ANALYSIS_SUB_PATH,
                {equipmentId}
              )}`
          : undefined
      }
    />
  )
}

/** The loader the pages show while their data loads (DataContentWrapper), for route-level waits */
const PageLoader = () => (
  <PageContainer>
    <DataContentWrapper showLoader isError={false} isNoData={false}>
      {null}
    </DataContentWrapper>
  </PageContainer>
)

const SuiteRouteWrapper = ({children}: {children: ReactNode}) => {
  return (
    <ThemeProvider theme={useFundamentalMuiTheme()}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Suspense fallback={<PageLoader />}>{children}</Suspense>
      </LocalizationProvider>
    </ThemeProvider>
  )
}

const SuitePageLoader = () => (
  <SuiteRouteWrapper>
    <PageLoader />
  </SuiteRouteWrapper>
)

const SuitePlantStatusRouteWrapper = ({isFeaturePlantView = false}) => (
  <SuiteRouteWrapper>
    <SuitePlantStatusPage isFeaturePlantView={isFeaturePlantView} />
  </SuiteRouteWrapper>
)

/**
 * Global / area / country performance overview. Drill-downs stay inside the feature
 * (`:locationId` takes a KPI dashboard location id), while plant rows switch the Suite
 * hierarchy to the plant so the Plant Status page is shown.
 */
const commentErrorMessageKeys: Partial<Record<ActualVsPlannedErrorSource, string>> = {
  comments: 'comments.message.failedToFetchComments',
  createComment: 'comments.message.failedToAddComment'
}

/**
 * Surfaces failed comment requests as a notification. KPI errors need none: the page already shows
 * them with a retry.
 */
const useActualVsPlannedErrorNotification = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {notify} = useNotification()

  return useCallback(
    (_error: unknown, source: ActualVsPlannedErrorSource) => {
      const messageKey = commentErrorMessageKeys[source]
      if (messageKey) notify('error', t(messageKey))
    },
    [notify, t]
  )
}

const SuiteActualVsPlannedPage = ({locationId}: {locationId?: string}) => {
  const hierarchyId = useSuiteHierarchyId()
  const globalHierarchyId = useGlobalHierarchyId()
  const plantsMap = useUpmPlantsContext({isInitialLoad: true})
  const {getUrl: getKpiLocationSuiteUrl} = useKpiLocationSuiteUrl()

  // Every level switches the picker along: to the Suite node of the area / country, or for KPI-only
  // locations without one, to the location below the selected node. Locations the user has no
  // access to get no link, so e.g. a country user sees no "back to area"
  const routes = useMemo<ActualVsPlannedRoutes>(() => {
    const baseUrl = buildFeatureUrl(PERFORMANCE_REPORTS_FEATURE_NAME, hierarchyId)
    const getLocationUrl = (id: string) => getKpiLocationSuiteUrl(id, hierarchyId)
    const plants = Object.values(plantsMap ?? {})

    return {
      global: buildFeatureUrl(PERFORMANCE_REPORTS_FEATURE_NAME, globalHierarchyId ?? hierarchyId),
      area: getLocationUrl,
      location: getLocationUrl,
      plant: (plantId) => {
        const upmId = plants.find((plant) => plant.plantId === plantId)?.upmId
        return upmId ? buildFeatureUrl(PERFORMANCE_REPORTS_FEATURE_NAME, upmId) : baseUrl
      }
    }
  }, [hierarchyId, globalHierarchyId, plantsMap, getKpiLocationSuiteUrl])

  const onError = useActualVsPlannedErrorNotification()

  return (
    <SuiteRouteWrapper>
      <ActualVsPlannedPage
        locationId={locationId}
        routes={routes}
        showBackToGlobal={!!globalHierarchyId}
        onError={onError}
      />
    </SuiteRouteWrapper>
  )
}

const breadcrumbLabelKeys: Partial<Record<HierarchyNodeType, string>> = {
  [HierarchyNodeType.Area]: 'breadcrumbs.area',
  [HierarchyNodeType.Region]: 'breadcrumbs.region',
  [HierarchyNodeType.Country]: 'breadcrumbs.country',
  [HierarchyNodeType.Plant]: 'breadcrumbs.plant'
}

/** Names a URL segment holding a KPI location id by its level ("Area", "Country", …) */
const useKpiLocationBreadcrumbLabel = (locationId?: string) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {data: kpiHierarchy} = useHierarchy()
  const type =
    kpiHierarchy && locationId ? findLocationEntry(kpiHierarchy, locationId)?.type : undefined
  const labelKey = type && breadcrumbLabelKeys[type]

  useSetBreadcrumbLabel(locationId, labelKey ? t(labelKey) : undefined)
}

/**
 * Crumbs of a KPI location: `<area>` for areas, `<area>/<country>` for regions and countries and
 * `<area>/<country>/<plant>` for plants, so Suite's breadcrumb reads "Area | Country | Plant" with
 * each crumb leading to its overview. Undefined for locations missing in the KPI hierarchy and for
 * users who may not open a crumb above the location (the same rule as the back button), whose
 * pages then stay on the node's plain URL without crumbs.
 */
const useKpiLocationCrumbsPath = (locationId?: string) => {
  const {data: kpiHierarchy, isLoading: isKpiHierarchyLoading} = useHierarchy()
  const {getParentUrl, isLoading} = useParentLocationUrl()

  const path = kpiHierarchy && locationId ? getKpiLocationPath(kpiHierarchy, locationId) : []
  const canOpenCrumbs = path.filter(({id}) => id !== locationId).every(({id}) => !!getParentUrl(id))

  return {
    crumbsPath: path.length && canOpenCrumbs ? path.map(({id}) => id) : undefined,
    isLoading: isLoading || isKpiHierarchyLoading
  }
}

/** The node's URL with the crumbs of `useKpiLocationCrumbsPath`, if any */
const getCrumbsUrl = (hierarchyId: string | null, crumbsPath?: string[]) => {
  const rootUrl = buildFeatureUrl(PERFORMANCE_REPORTS_FEATURE_NAME, hierarchyId)
  return crumbsPath ? `${rootUrl}/${crumbsPath.join('/')}` : rootUrl
}

/** `:areaId` or `:areaId/:locationId` drill-down, shown as "Area" / "Area | Country" */
const SuiteActualVsPlannedDrillDown = () => {
  const {areaId, locationId = areaId} = useParams<{areaId?: string; locationId?: string}>()
  const hierarchyId = useSuiteHierarchyId()
  const plantsMap = useUpmPlantsContext({isInitialLoad: true})
  const {
    getUrl: getKpiLocationSuiteUrl,
    getSuiteNodeId,
    isWithinNode,
    isLoading
  } = useKpiLocationSuiteUrl()
  const {crumbsPath, isLoading: isCrumbsPathLoading} = useKpiLocationCrumbsPath(locationId)
  const {pathname, search} = useLocation()

  useKpiLocationBreadcrumbLabel(areaId)
  // Register each segment once: on the area route both params are the area, and when navigating
  // area → country (same component) the second hook's cleanup would otherwise remove the area label
  useKpiLocationBreadcrumbLabel(locationId !== areaId ? locationId : undefined)

  if (!plantsMap || isLoading || isCrumbsPathLoading) {
    return <SuitePageLoader />
  }

  // The picker matches the page: a location with its own Suite node (e.g. reached via the area /
  // country crumbs) opens that node
  const locationNodeId = locationId && getSuiteNodeId(locationId)
  if (locationNodeId && locationNodeId !== hierarchyId) {
    return (
      <Navigate
        replace
        to={`${buildFeatureUrl(PERFORMANCE_REPORTS_FEATURE_NAME, locationNodeId)}${search}`}
      />
    )
  }

  // The selected node's own area / country keeps its crumbs only when the user may open them
  // (e.g. a bookmark of a user without access to the area falls back to the plain URL)
  const crumbsUrl = getCrumbsUrl(hierarchyId, crumbsPath)
  if (locationNodeId && pathname.replace(/\/+$/, '') !== crumbsUrl) {
    return <Navigate replace to={`${crumbsUrl}${search}`} />
  }

  // A KPI-only location shows below the selected node only when it is part of it (e.g. "US Midwest"
  // of US, never a plant's country): others open below the Global node, or, without access to it,
  // fall back to the selected node's root
  if (!locationId || !isWithinNode(locationId, hierarchyId)) {
    const target =
      (locationId && getKpiLocationSuiteUrl(locationId)) ||
      buildFeatureUrl(PERFORMANCE_REPORTS_FEATURE_NAME, hierarchyId)
    return <Navigate replace to={`${target}${search}`} />
  }

  return <SuiteActualVsPlannedPage locationId={locationId} />
}

/** Plant nodes open at their crumbs (`<area>/<country>/<plant>`), see `useKpiLocationCrumbsPath` */
const SuitePlantEntry = () => {
  const hierarchyId = useSuiteHierarchyId()
  const upmPlant = usePlantFromHierarchyId()
  const {crumbsPath, isLoading} = useKpiLocationCrumbsPath(upmPlant?.plantId)
  const {search} = useLocation()

  if (!upmPlant || isLoading) {
    return <SuitePageLoader />
  }

  if (crumbsPath) {
    return <Navigate replace to={`${getCrumbsUrl(hierarchyId, crumbsPath)}${search}`} />
  }

  return <SuitePlantStatusRouteWrapper isFeaturePlantView />
}

/** `:areaId/:locationId/:plantId` — names the crumbs and hosts the plant pages */
const SuitePlantBreadcrumbRoute = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const hierarchyId = useSuiteHierarchyId()
  const plantsMap = useUpmPlantsContext({isInitialLoad: true})
  const upmPlant = usePlantFromHierarchyId()
  const {crumbsPath, isLoading} = useKpiLocationCrumbsPath(upmPlant?.plantId)
  const {search} = useLocation()
  const {areaId, locationId, plantId} = useParams<{
    areaId: string
    locationId: string
    plantId: string
  }>()

  useKpiLocationBreadcrumbLabel(areaId)
  useKpiLocationBreadcrumbLabel(locationId)
  useSetBreadcrumbLabel(plantId, t('breadcrumbs.plant'))

  if (!plantsMap) {
    return <SuitePageLoader />
  }

  const rootUrl = buildFeatureUrl(PERFORMANCE_REPORTS_FEATURE_NAME, hierarchyId)

  // the plant path only makes sense while a plant is selected in the Suite hierarchy
  if (!Object.values(plantsMap).some((plant) => plant.upmId === hierarchyId)) {
    return <Navigate replace to={rootUrl} />
  }

  if (!upmPlant || isLoading) {
    return <SuitePageLoader />
  }

  // e.g. a bookmarked plant path of a user without access to its area / country: no crumbs to them
  if (!crumbsPath) {
    return <Navigate replace to={`${rootUrl}${search}`} />
  }

  return <Outlet />
}

/**
 * Overview of the selected non-plant Suite node, mapped to its KPI dashboard location. Area and
 * country nodes open at their crumbs (`<area>` / `<area>/<country>`), see
 * `useKpiLocationCrumbsPath`.
 */
const SuiteSelectedLocationOverview = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const hierarchyId = useSuiteHierarchyId()
  const {isLoading, resolution} = useSuiteKpiLocation(hierarchyId)
  const locationId = resolution?.type === 'location' ? resolution.locationId : undefined
  const {getSuiteNodeId, isLoading: isSuiteUrlLoading} = useKpiLocationSuiteUrl()
  const {crumbsPath, isLoading: isCrumbsPathLoading} = useKpiLocationCrumbsPath(locationId)
  const {search} = useLocation()

  if (isLoading || !resolution || isSuiteUrlLoading || isCrumbsPathLoading) {
    return <SuitePageLoader />
  }

  // Only when the location's crumbs lead back to this node, so the picker stays on it
  if (crumbsPath && locationId && getSuiteNodeId(locationId) === hierarchyId) {
    return <Navigate replace to={`${getCrumbsUrl(hierarchyId, crumbsPath)}${search}`} />
  }

  if (resolution.type === 'notFound') {
    return (
      <SuiteRouteWrapper>
        <PageContainer>
          <TextRegular className="text-foreground" data-test-id="ac-vs-pl-no-kpi-location">
            {t('actualVsPlanned.noKpiLocation')}
          </TextRegular>
        </PageContainer>
      </SuiteRouteWrapper>
    )
  }

  return <SuiteActualVsPlannedPage locationId={locationId} />
}

/** Plant nodes show the Plant Status page, every other hierarchy level the performance overview */
const SuitePerformanceReportsIndex = () => {
  const hierarchyId = useSuiteHierarchyId()
  const plantsMap = useUpmPlantsContext({isInitialLoad: true})

  if (!plantsMap) {
    return <SuitePageLoader />
  }

  const isPlant = Object.values(plantsMap).some((plant) => plant.upmId === hierarchyId)

  return isPlant ? <SuitePlantEntry /> : <SuiteSelectedLocationOverview />
}

/**
 * The Suite hierarchy picker swaps only the `hierarchyId` segment and keeps the rest of the URL.
 * Here that rest is a KPI location (`<area>/<country>/<plant>`) or a Stoppage Analysis page of the
 * previously selected node, so a picker switch restarts at the new node's root, where the index
 * route opens the plant page or the location overview.
 */
const HierarchyPickerChangeReset = () => {
  const hierarchyId = useSuiteHierarchyId()
  const isPickerSwitch = useIsHierarchyPickerSwitch()
  const {pathname, search} = useLocation()
  const rootUrl = buildFeatureUrl(PERFORMANCE_REPORTS_FEATURE_NAME, hierarchyId)

  if (isPickerSwitch && pathname.replace(/\/+$/, '') !== rootUrl) {
    return <Navigate replace to={`${rootUrl}${search}`} />
  }

  return <Outlet />
}

/**
 * Plant Status embedded in another feature (e.g. a dashboard) only exists for plants: other nodes
 * restart at the host feature's root. A picker switch to another plant drops a Stoppage Analysis
 * sub-page, as its equipment belongs to the previous plant.
 */
const EmbeddedPlantStatusGuard = () => {
  const hierarchyId = useSuiteHierarchyId()
  const plantsMap = useUpmPlantsContext({isInitialLoad: true})
  const isPickerSwitch = useIsHierarchyPickerSwitch()
  const {pathname, search} = useLocation()
  const hostRootUrl = useResolvedPath('..').pathname
  const plantStatusUrl = useResolvedPath('.').pathname

  if (!plantsMap) {
    return <SuitePageLoader />
  }

  if (!Object.values(plantsMap).some((plant) => plant.upmId === hierarchyId)) {
    return <Navigate replace to={`${hostRootUrl}${search}`} />
  }

  if (isPickerSwitch && pathname.replace(/\/+$/, '') !== plantStatusUrl) {
    return <Navigate replace to={`${plantStatusUrl}${search}`} />
  }

  return <Outlet />
}

/**
 * A composable sub-route for the PlantStatus page.
 *
 * Designed to be nested inside any parent route whose page navigates to
 * `PLANT_STATUS_SUB_PATH` in it's feature.
 * By embedding this sub-route, the PlantStatus page becomes a child of the caller's
 * route tree, which preserves "back to" navigation and allows seamless integration
 * into other features without duplicating route definitions.
 *
 * Includes its own `FeatureTranslationsLoader` so it's self-contained — translations
 * are loaded regardless of whether the parent feature loads them.
 */
export const performanceReportsPlantStatusSubRoute: RouteObject = {
  path: PLANT_STATUS_SUB_PATH,
  element: (
    <FeatureTranslationsLoader translations={performanceReportsFeatureTranslations}>
      <NotificationProvider>
        <FeatureErrorBoundary>
          <EmbeddedPlantStatusGuard />
        </FeatureErrorBoundary>
      </NotificationProvider>
    </FeatureTranslationsLoader>
  ),
  children: [
    {
      index: true,
      element: <SuitePlantStatusRouteWrapper />
    },
    stoppageAnalysisSubRoute
  ]
}

export const performanceReportsRoutes: RouteObject = {
  path: '*',
  element: (
    <FeatureTranslationsLoader translations={performanceReportsFeatureTranslations}>
      <NotificationProvider>
        <FeatureErrorBoundary>
          <HierarchyPickerChangeReset />
        </FeatureErrorBoundary>
      </NotificationProvider>
    </FeatureTranslationsLoader>
  ),
  children: [
    {
      index: true,
      element: <SuitePerformanceReportsIndex />
    },
    // Static sub-routes (StoppageAnalysis) outrank these dynamic segments in react-router matching
    {
      path: ':areaId',
      element: <SuiteActualVsPlannedDrillDown />
    },
    {
      path: ':areaId/:locationId',
      element: <SuiteActualVsPlannedDrillDown />
    },
    {
      path: ':areaId/:locationId/:plantId',
      element: <SuitePlantBreadcrumbRoute />,
      children: [
        {
          index: true,
          element: <SuitePlantStatusRouteWrapper isFeaturePlantView />
        }
      ]
    },
    stoppageAnalysisSubRoute
  ]
}
